import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock, MapPin, Monitor, User, ChevronRight, GraduationCap, ChevronLeft } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { UpcomingSession } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';

interface UpcomingSessionsWidgetProps {
  sessions: UpcomingSession[];
  loading?: boolean;
}

const UpcomingSessionsWidget = ({
  sessions,
  loading = false
}: UpcomingSessionsWidgetProps) => {
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const cardsPerView = 4; // Número de cards visíveis por vez
  const maxIndex = Math.max(0, sessions.length - cardsPerView);

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current ? scrollContainerRef.current.clientWidth / 4 : 0; // Largura do container dividido por 4
      scrollContainerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
    setCurrentIndex(index);
  };

  const handlePrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return <Monitor className="h-2.5 w-2.5" />;
      case 'in-person':
        return <MapPin className="h-2.5 w-2.5" />;
      default:
        return <Calendar className="h-2.5 w-2.5" />;
    }
  };

  const getModeLabel = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return 'Online';
      case 'in-person':
        return 'Presencial';
      default:
        return mode;
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'C';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Function to handle image loading errors
  const handleImageError = (sessionId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [sessionId]: true
    }));
  };

  const getCustomerData = (session: UpcomingSession) => {
    const customer = session.class.subscription?.subscription_customers?.[0]?.customer;
    return {
      name: customer?.name || 'Cliente não identificado',
      domain: customer?.domain || null
    };
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
            <Calendar className="h-4 w-4 text-blue-600" />
            Próximas Sessões
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-4">
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
          <Calendar className="h-4 w-4 text-blue-600" />
          Próximas Sessões
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/classes')}
          className="h-7 px-2 text-sm font-extrabold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          Ver todas
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-4">
        {sessions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhuma sessão agendada</p>
          </div>
        ) : (
          <div className="relative">
            {/* Navigation Buttons */}
            {sessions.length > cardsPerView && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border-gray-200 hover:bg-gray-50"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border-gray-200 hover:bg-gray-50"
                  onClick={handleNext}
                  disabled={currentIndex >= maxIndex}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            
            {/* Cards Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 px-1"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                minWidth: '100%' // Container ocupa toda a largura disponível
              }}
            >
              {sessions.map((session) => {
                const customerData = getCustomerData(session);
                const instructorName = session.instructor 
                  ? `${session.instructor.first_name} ${session.instructor.last_name}`
                  : 'Instrutor não definido';
                
                return (
                  <div
                    key={session.id}
                    className="group relative p-2 rounded-lg border border-gray-100 bg-white hover:border-blue-200 hover:shadow-md cursor-pointer transition-all duration-200 w-[calc(25%-18px)] flex-shrink-0 min-h-[120px]"
                    onClick={() => navigate(`/classes/${session.class.id}`)}
                  >
                    <div className="flex items-start gap-2">
                      {/* Customer Avatar */}
                      <Avatar className="h-8 w-8 border border-gray-100 group-hover:border-blue-200 transition-colors flex-shrink-0">
                        {customerData.domain && !failedImages[session.id] ? (
                          <AvatarImage 
                            src={`https://cdn.brandfetch.io/${customerData.domain}?c=1idk4AUs2-buvooOUvQ`} 
                            alt={customerData.name}
                            onError={() => handleImageError(session.id)}
                          />
                        ) : (
                          <AvatarFallback className="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 font-extrabold text-[10px]">
                            {getInitials(customerData.name)}
                          </AvatarFallback>
                        )}
                      </Avatar>

                      {/* Session Details */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {/* Course Name and Customer */}
                        <div>
                          <h4 className="font-extrabold text-gray-900 text-xs leading-tight group-hover:text-blue-700 transition-colors line-clamp-1">
                            {session.class.course.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {customerData.name}
                          </p>
                        </div>

                        {/* Compact Info Row */}
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          {/* Date */}
                          <div className="flex items-center gap-1">
                            <Calendar className="h-2.5 w-2.5 text-gray-400" />
                            <span>{format(parseISO(session.start_date), "dd/MM", { locale: ptBR })}</span>
                          </div>
                          
                          {/* Time */}
                          <div className="flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5 text-gray-400" />
                            <span>{format(parseISO(session.start_date), "HH:mm", { locale: ptBR })}</span>
                          </div>

                          {/* Mode */}
                          <div className="flex items-center gap-1">
                            {getModeIcon(session.mode)}
                            <span>{getModeLabel(session.mode)}</span>
                          </div>
                        </div>

                        {/* Instructor */}
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <User className="h-2.5 w-2.5 text-gray-400" />
                          <span className="truncate">{instructorName}</span>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingSessionsWidget;
